import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { adminApi } from '../api';
import { useAdmin } from './AdminContext';
import VeritageLogo from '../components/VeritageLogo';
import Dropdown from '../components/Dropdown';
import Modal, { Field } from './Modal';
import { ArrowUpRight, ChevronDown, ChevronUp, Logout, Plus, Settings } from '../components/Icons';

const SEL_KEY = 'veritage_docs_admin_sel';

function readSel() {
  try { return JSON.parse(localStorage.getItem(SEL_KEY)) || {}; } catch { return {}; }
}

export function pageState(p) {
  if (!p.published) return { key: 'draft', label: 'Draft' };
  if (p.hasUnpublishedChanges) return { key: 'changed', label: 'Unpublished changes' };
  return { key: 'live', label: 'Live' };
}

function groupBySection(pages) {
  const groups = [];
  const byName = new Map();
  pages.forEach((p) => {
    if (!byName.has(p.section)) { const g = { section: p.section, pages: [] }; byName.set(p.section, g); groups.push(g); }
    byName.get(p.section).pages.push(p);
  });
  return groups;
}

function NewPageModal({ version, onClose, onCreated }) {
  const sections = useMemo(() => [...new Set(version.pages.map((p) => p.section))], [version]);
  const [title, setTitle] = useState('');
  const [section, setSection] = useState(sections[0] || 'General');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const page = await adminApi.createPage(version.versionId, {
        title,
        section,
        content: 'Write an introduction here.\n\n## First section\n\nContent…\n',
      });
      onCreated(page);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New page"
      onClose={onClose}
      footer={<>
        <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" onClick={onClose}>Cancel</button>
        <button type="submit" form="new-page" className="vt-btn vt-btn--sm" disabled={busy || !title.trim()}>Create draft</button>
      </>}
    >
      <form id="new-page" onSubmit={create} className="cms-form">
        {error && <div className="cms-alert">{error}</div>}
        <Field label="Title"><input className="cms-input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Webhooks" /></Field>
        <Field label="Sidebar section" hint="Pick an existing section or type a new one.">
          <input className="cms-input" list="cms-sections" value={section} onChange={(e) => setSection(e.target.value)} />
          <datalist id="cms-sections">{sections.map((s) => <option key={s} value={s} />)}</datalist>
        </Field>
        <p className="cms-muted">New pages start as drafts and are only visible on the public site once you publish them.</p>
      </form>
    </Modal>
  );
}

export default function AdminLayout() {
  const { auth, logout, tree, refreshTree } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [sel, setSel] = useState(readSel);
  const [newPageOpen, setNewPageOpen] = useState(false);

  // Keep the selected product/version in sync with the page being edited.
  const editingId = location.pathname.match(/\/admin\/pages\/([^/]+)/)?.[1];
  useEffect(() => {
    if (!tree || !editingId) return;
    for (const p of tree) for (const v of p.versions) {
      if (v.pages.some((pg) => pg.id === editingId)) {
        if (sel.productId !== p.id || sel.versionKey !== v.id) setSel({ productId: p.id, versionKey: v.id });
        return;
      }
    }
  }, [tree, editingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { try { localStorage.setItem(SEL_KEY, JSON.stringify(sel)); } catch {} }, [sel]);

  if (auth.status === 'checking') return <div className="docs-fullscreen-msg"><div className="docs-spinner" /></div>;
  if (auth.status !== 'admin') return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;

  const product = tree?.find((p) => p.id === sel.productId) || tree?.[0];
  const version = product?.versions.find((v) => v.id === sel.versionKey) || product?.versions[0];
  const groups = version ? groupBySection(version.pages) : [];

  const move = async (page, dir) => {
    const list = [...version.pages];
    const i = list.findIndex((p) => p.id === page.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    // Moving across a section boundary adopts the neighbour's section.
    const neighbour = list[j];
    [list[i], list[j]] = [list[j], list[i]];
    const order = list.map((p, idx) => ({
      id: p.id,
      position: idx,
      section: p.id === page.id && neighbour.section !== page.section ? neighbour.section : p.section,
    }));
    await adminApi.reorder(version.versionId, order);
    refreshTree();
  };

  const publicUrl = (p) => `/${product.id}/${version.id}/${p.slug}`;

  return (
    <div className="cms">
      <header className="cms-topbar">
        <Link to="/admin" className="docs-nav-brand">
          <VeritageLogo height={24} />
          <span className="docs-nav-divider" aria-hidden="true" />
          <span className="docs-nav-title">Docs CMS</span>
        </Link>
        <div className="cms-topbar-right">
          <a className="vt-nav-link" href="/" target="_blank" rel="noreferrer">View docs <ArrowUpRight size={14} /></a>
          <span className="cms-user">{auth.email}</span>
          <button type="button" className="docs-icon-btn" onClick={logout} title="Sign out" aria-label="Sign out"><Logout size={18} /></button>
        </div>
      </header>

      <div className="cms-body">
        <aside className="cms-sidebar">
          {tree && tree.length === 0 && (
            <div className="cms-empty-side">No products yet. <Link to="/admin">Create one</Link>.</div>
          )}
          {product && (
            <div className="cms-sidebar-pickers">
              <Dropdown label="Product" value={product.id} options={tree} onChange={(id) => setSel({ productId: id })} minWidth={200} />
              {version && (
                <Dropdown
                  label="Version"
                  value={version.id}
                  options={product.versions}
                  onChange={(id) => setSel({ productId: product.id, versionKey: id })}
                />
              )}
            </div>
          )}

          {version && (
            <>
              <button type="button" className="cms-new-page" onClick={() => setNewPageOpen(true)}>
                <Plus size={16} /> New page
              </button>
              <nav className="cms-tree">
                {groups.map((g) => (
                  <div key={g.section} className="cms-tree-group">
                    <div className="vt-eyebrow cms-tree-label">{g.section}</div>
                    {g.pages.map((p) => {
                      const st = pageState(p);
                      return (
                        <div key={p.id} className="cms-tree-row">
                          <NavLink to={`/admin/pages/${p.id}`} className={({ isActive }) => `cms-tree-item${isActive ? ' is-active' : ''}`}>
                            <span className={`cms-dot cms-dot--${st.key}`} title={st.label} />
                            <span className="cms-tree-title">{p.title}</span>
                          </NavLink>
                          <span className="cms-tree-actions">
                            <button type="button" onClick={() => move(p, -1)} aria-label={`Move ${p.title} up`}><ChevronUp size={14} /></button>
                            <button type="button" onClick={() => move(p, 1)} aria-label={`Move ${p.title} down`}><ChevronDown size={14} /></button>
                            {p.published && <a href={publicUrl(p)} target="_blank" rel="noreferrer" aria-label={`Open ${p.title} on the docs site`}><ArrowUpRight size={14} /></a>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </nav>
            </>
          )}

          <div className="cms-sidebar-foot">
            <NavLink end to="/admin" className={({ isActive }) => `cms-tree-item${isActive ? ' is-active' : ''}`}>
              <Settings size={16} /> Products &amp; versions
            </NavLink>
            <div className="cms-legend">
              <span><i className="cms-dot cms-dot--live" /> Live</span>
              <span><i className="cms-dot cms-dot--changed" /> Changes</span>
              <span><i className="cms-dot cms-dot--draft" /> Draft</span>
            </div>
          </div>
        </aside>

        <section className="cms-content">
          <Outlet />
        </section>
      </div>

      {newPageOpen && version && (
        <NewPageModal
          version={version}
          onClose={() => setNewPageOpen(false)}
          onCreated={async (page) => {
            setNewPageOpen(false);
            await refreshTree();
            navigate(`/admin/pages/${page.id}`);
          }}
        />
      )}
    </div>
  );
}
